import React, { useState, useMemo, useEffect } from 'react'
import {
  DomainManagementStyled,
  DomainManagementTop,
  TopLeft,
  TopRight,
  DomainManagementSearch,
} from 'Features/domainsManagement/routes/DomainManagement/DomainManagement.styled'
import { useTranslation } from 'react-i18next'
import { Search } from 'Assets/icons/Search'
import { useAppStore } from 'Stores/appStoreModuleFed'
import {
  InputWithIcon,
  ButtonWithIcon,
  TableWithPagination,
  CheqLoader,
  theme,
} from '@clickcease/cc-client-components-lib'
import { GLOBAL_MODALS } from 'Types/GlobalModal'
import { Domain } from 'Types/Domain'
import { NoActiveDomainProductsModal } from 'Features/domainsManagement/components/NoActiveDomainProductsModal/NoActiveDomainProductsModal'
import { useGetPlansDetails } from 'Features/accountSettings/api/getPlansDetails'
import { PRODUCT_TYPES } from 'Types/ProductTypes'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDomainsPagination } from 'Features/domainsManagement/hooks/useDomainsPagination'
import useMainWrapperStore, { TRIGGER_OPTIONS } from 'Features/MainWrapper/stores/mainWrapper'
import { useUserStore } from 'Stores/userStoreModuleFed'
import { AddCircle } from 'Assets/icons/AddCircle'

interface LocationState {
  cameFromSideMenu?: boolean
}

export const DomainManagement = () => {
  const { t } = useTranslation('domainManagement')
  const { setModalToShow } = useAppStore()
  const { isTopBannerVisible, setTriggerLocation } = useMainWrapperStore()
  const { isReactivation, owner } = useUserStore()
  const location = useLocation()
  const locationState = location.state as LocationState
  const [isNoActiveDomainProductsModalOpen, setIsNoActiveDomainProductsModalOpen] =
    useState<boolean>(false)
  const [isModalClosed, setIsModalClosed] = useState(false)
  const {
    tableProps,
    searchText,
    onSearchInputChange,
    domains,
    isFetching,
    shouldShowUpgradeButton,
  } = useDomainsPagination()
  const { data: planDetails } = useGetPlansDetails()

  const navigate = useNavigate()
  const hasPaidMarketing = planDetails?.plans?.some(
    (product) => product.product.toLowerCase() === PRODUCT_TYPES.PAID_MARKETING
  )
  const hasBotZapping = planDetails?.plans?.some(
    (product) => product.product.toLowerCase() === PRODUCT_TYPES.BOT_ZAPPING
  )

  const noActiveDomainPM = (domains as Domain[])?.every((domain) => domain.paidMarketing === false)
  const noActiveDomainBM = (domains as Domain[])?.every((domain) => domain.botZapping === false)

  const shouldOpenModal = useMemo(() => {
    return (
      domains.length > 0 &&
      ((hasPaidMarketing && noActiveDomainPM) || (hasBotZapping && noActiveDomainBM))
    )
  }, [domains, noActiveDomainPM, noActiveDomainBM, hasBotZapping, hasPaidMarketing])

  const shouldShowReactivateModal = useMemo(() => {
    return isReactivation && noActiveDomainPM && noActiveDomainBM && !isFetching
  }, [isReactivation, noActiveDomainPM, noActiveDomainBM, isFetching])

  useEffect(() => {
    if (shouldShowReactivateModal) {
      setModalToShow(GLOBAL_MODALS.DOMAIN_REACTIVATION)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowReactivateModal])

  useEffect(() => {
    setIsNoActiveDomainProductsModalOpen(shouldOpenModal as boolean)
  }, [shouldOpenModal])

  const handleModalClose = (value: boolean) => setIsModalClosed(value)
  const navigateToModifyPlanPage = () => {
    navigate('/modify-plan')
  }

  const handleOpenAddDomainModal = () => {
    setModalToShow(GLOBAL_MODALS.ADD_DOMAIN)
    setTriggerLocation(TRIGGER_OPTIONS.DOMAIN_MANAGEMENT)
  }

  return (
    <>
      <DomainManagementStyled id={'domain-management'} isTopBannerVisible={isTopBannerVisible}>
        <DomainManagementTop>
          <TopLeft>
            <DomainManagementSearch>
              <InputWithIcon
                value={searchText}
                placeholder={t('search')}
                onChange={onSearchInputChange}
                iconLeft={<Search />}
              />
            </DomainManagementSearch>
          </TopLeft>
          <TopRight>
            {shouldShowUpgradeButton && (
              <ButtonWithIcon text={t('upgrade_plan')} onClick={navigateToModifyPlanPage} />
            )}
            {owner && (
              <ButtonWithIcon
                iconLeft={
                  <AddCircle color={theme.colors.primaryColor.value} width={20} height={20} />
                }
                variant="outline"
                text={t('add_domain')}
                onClick={handleOpenAddDomainModal}
              />
            )}
          </TopRight>
        </DomainManagementTop>
        {isFetching ? (
          <CheqLoader />
        ) : (
          <TableWithPagination {...tableProps} wrapperStyle={{ height: '100%' }} />
        )}
      </DomainManagementStyled>
      {locationState?.cameFromSideMenu && !isModalClosed && domains && (
        <NoActiveDomainProductsModal
          domains={domains}
          isOpen={isNoActiveDomainProductsModalOpen}
          onOpenChange={(isOpen) => {
            setIsNoActiveDomainProductsModalOpen(isOpen)
          }}
          onModalClose={handleModalClose}
        />
      )}
    </>
  )
}
